import type { ReactElement } from 'react';
import {
  Datagrid,
  FunctionField,
  List,
  NumberInput,
  SearchInput,
  SelectInput,
  TextField,
  TextInput,
} from 'react-admin';
import { Alert, Avatar, Box, Chip, Stack, Typography } from '@mui/material';
import CheckroomIcon from '@mui/icons-material/Checkroom';
import { SearchableFilterActions, scrollableListSx } from '@src/components/indexPage';
import { getCurrentPartner } from '@src/providers/dataProvider/shared';
import { DashboardResources } from '@src/types';

import { CUSTODY_STATUSES, INERT_FILTERS_ENABLED, VAS_JOB_TYPES, VAS_STATUSES } from './constants';
import { GRAPHQL_INT_MAX, GRAPHQL_INT_MIN } from './dataProvider';
import { displayNameFor, formatCents, formatDateTime } from './format';
import InventoryItemExpand from './InventoryItemExpand';
import InventoryItemListError from './InventoryItemListError';
import { useInventoryConstants } from './useInventoryConstants';
import type { ConstantOption, InventoryConstants, InventoryItemRecord } from './types';

const firstImage = (record: InventoryItemRecord): string | undefined => {
  if (record.state === 'PHOTOGRAPHY_PENDING' && !record.isImageFromInventory) return undefined;
  const images = record.thumbnailImageUrls ?? record.imageUrls;
  if (!images) return undefined;
  return images.front ?? images.main ?? Object.values(images)[0];
};

type StateColor = 'success' | 'error' | 'warning' | 'info' | 'default';

const stateColor = (state: string): StateColor => {
  if (/REJECTED|DAMAGED|MISSING|SHRINK|LOST|PROBLEM|WRONG/.test(state)) return 'error';
  if (/APPROVED|CURATED$/.test(state)) return 'success';
  if (/ORDERED|PICK|PACK|SHIPPED|DROPPED/.test(state)) return 'info';
  if (/PENDING|NEEDS|REVIEW|IN_PROGRESS|HOLD|CLEANING|REPAIR|PROCESSING/.test(state)) return 'warning';
  return 'default';
};

const toChoices = (options: ConstantOption[]) =>
  options.map(({ code, displayName }) => ({ id: code, name: displayName }));

const YES_NO = [{ id: 'true', name: 'Yes' }, { id: 'false', name: 'No' }];

/**
 * reware-api takes these as GraphQL `Int`, so a decimal or out-of-range value
 * would fail the whole query. The data provider drops such values, which would
 * otherwise show the user an unfiltered list that looks filtered — so reject
 * them here, where the message can point at the offending input.
 */
export const isGraphQLInt = (value: unknown): string | undefined => {
  if (value == null || value === '') return undefined;
  const parsed = Number(value);
  if (!Number.isInteger(parsed)) return 'Must be a whole number';
  if (parsed < GRAPHQL_INT_MIN || parsed > GRAPHQL_INT_MAX) {
    return `Must be between ${GRAPHQL_INT_MIN} and ${GRAPHQL_INT_MAX}`;
  }
  return undefined;
};

/**
 * Dollars are rounded to whole cents before matching, so more than two decimal
 * places would silently search for a price the user didn't type.
 *
 * Compared with a tolerance rather than exactly: `19.99 * 100` is
 * 1998.9999999999998 in binary floating point, so an exact test would reject
 * most valid prices.
 */
export const isPrice = (value: unknown): string | undefined => {
  if (value == null || value === '') return undefined;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 'Must be a number';
  const cents = parsed * 100;
  if (Math.abs(cents - Math.round(cents)) > 1e-6) return 'At most 2 decimal places';
  if (Math.abs(Math.round(cents)) > GRAPHQL_INT_MAX) return 'Price is too large';
  return undefined;
};

// Repair / cleaning / photography job status as one select: reware-api only
// applies one of them per query (each replaces the item-id set), so a single
// combined input keeps the filter honest. The data provider splits the value.
const VAS_STATUS_CHOICES = VAS_JOB_TYPES.flatMap(({ code: type, displayName: typeName }) =>
  VAS_STATUSES.map(({ code, displayName }) => ({
    id: `${type}:${code}`,
    name: `${typeName} · ${displayName}`,
  })));

/**
 * The filter inputs, ordered for the Add filter menu: everyday item lookups
 * first, then VAS / identifier / location filters mostly used by ops.
 *
 * Exported so a test can walk every `source` and check the data provider
 * actually forwards it — a renamed source otherwise renders a filter that
 * stores a value and silently never reaches the API.
 */
export const buildInventoryFilters = ({
  states,
  conditions,
  rejectReasons,
  availabilities,
  checkinAvailabilities,
  locationTypes,
  wmsStatuses,
  warehouses,
  departments,
  categories,
}: InventoryConstants): ReactElement<{ source: string }>[] => [
  <SearchInput key="searchQuery" source="searchQuery" placeholder="Search items" alwaysOn />,
  <SelectInput key="state" source="state" label="State" choices={toChoices(states)} />,
  <SelectInput key="condition" source="condition" label="Condition" choices={toChoices(conditions)} />,
  <TextInput key="sku" source="sku" label="SKU" />,
  <TextInput
    key="parentSku"
    source="parentSku"
    label="Parent SKU"
    helperText="Ignored when a Shopify ID is also set"
  />,
  <TextInput key="brand" source="brand" label="Brand" />,
  <NumberInput key="itemId" source="itemId" label="Item ID" validate={isGraphQLInt} />,
  <TextInput key="uuid" source="uuid" label="Item UUID" helperText="Full UUID" />,
  <TextInput key="troveSerialNumber" source="troveSerialNumber" label="Serial number" />,
  <TextInput key="upc" source="upc" label="UPC" />,
  <TextInput key="partnerSku" source="partnerSku" label="Brand SKU" />,
  <TextInput key="title" source="title" label="Title" helperText="Exact title" />,
  <TextInput key="size" source="size" label="Size" />,
  <TextInput key="color" source="color" label="Color" />,
  <TextInput key="season" source="season" label="Season" />,
  <SelectInput key="department" source="department" label="Department" choices={toChoices(departments)} />,
  <SelectInput key="category" source="category" label="Category" choices={toChoices(categories)} />,
  <NumberInput
    key="price"
    source="price"
    label="Price ($)"
    step={0.01}
    helperText="Exact price, e.g. 24.99"
    validate={isPrice}
  />,
  <SelectInput
    key="rejectedReason"
    source="rejectedReason"
    label="Rejected reason"
    choices={[{ id: 'NONE', name: 'None' }, ...toChoices(rejectReasons)]}
  />,
  <SelectInput
    key="availability"
    source="availability"
    label="Storefront availability"
    choices={toChoices(availabilities)}
  />,
  <SelectInput
    key="availabilityCheckin"
    source="availabilityCheckin"
    label="Check-in availability"
    choices={toChoices(checkinAvailabilities)}
  />,
  <SelectInput key="custodyStatus" source="custodyStatus" label="Custody status" choices={toChoices(CUSTODY_STATUSES)} />,
  <SelectInput key="newWithTags" source="newWithTags" label="New with tags" choices={YES_NO} />,
  ...(INERT_FILTERS_ENABLED ?
    [
      <SelectInput key="isFlagged" source="isFlagged" label="Flagged" choices={YES_NO} />,
      <SelectInput key="needsPhotography" source="needsPhotography" label="Needs photography" choices={YES_NO} />,
    ] :
    []),
  <SelectInput key="needsRepair" source="needsRepair" label="Needs repair" choices={YES_NO} />,
  <SelectInput key="needsCleaning" source="needsCleaning" label="Needs cleaning" choices={YES_NO} />,
  <SelectInput
    key="vasStatus"
    source="vasStatus"
    label="Job status"
    choices={VAS_STATUS_CHOICES}
  />,
  <NumberInput key="catalogId" source="catalogId" label="Catalog item ID" validate={isGraphQLInt} />,
  <NumberInput key="shipmentId" source="shipmentId" label="Shipment ID" validate={isGraphQLInt} />,
  <TextInput key="resaleUpc" source="resaleUpc" label="Resale UPC" />,
  <TextInput key="preprintedLpn" source="preprintedLpn" label="Preprinted tag (LPN)" />,
  <TextInput
    key="shopifyProductId"
    source="shopifyProductId"
    label="Shopify product ID"
    // reware-api turns a matched ID into parent SKU / color filters (plus size
    // and condition for a variant); Size, Condition, and Search set here take
    // precedence. With no match it returns nothing. Parent SKU is dropped
    // alongside a Shopify ID (see dataProvider).
    helperText="Ignores Parent SKU; no results if no product matches"
  />,
  <TextInput
    key="shopifyVariantId"
    source="shopifyVariantId"
    label="Shopify variant ID"
    helperText="Ignores Parent SKU; no results if no variant matches"
  />,
  <TextInput key="fabric" source="fabric" label="Fabric" />,
  <SelectInput key="isKeyless" source="isKeyless" label="Keyless" choices={YES_NO} />,
  <SelectInput key="warehouseCode" source="warehouseCode" label="Warehouse" choices={toChoices(warehouses)} />,
  ...(INERT_FILTERS_ENABLED ?
    [<NumberInput key="locationId" source="locationId" label="Location ID" validate={isGraphQLInt} />] :
    []),
  <TextInput key="box" source="box" label="Box" />,
  <TextInput key="position" source="position" label="Position" helperText="Total count may be inaccurate" />,
  <SelectInput key="locationType" source="locationType" label="Location type" choices={toChoices(locationTypes)} />,
  <SelectInput key="wmsStatus" source="wmsStatus" label="WMS sync status" choices={toChoices(wmsStatuses)} />,
];

const InventoryItemList = () => {
  const { isFallback, ...constants } = useInventoryConstants();
  const partnerCode = getCurrentPartner()?.code;
  const { states, conditions, rejectReasons } = constants;
  const filters = buildInventoryFilters(constants);

  return (
    <>
      {isFallback && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          Some filter options couldn&apos;t be loaded, so filtering and item actions may be
          inaccurate. Reload the page to retry.
        </Alert>
      )}
      <List
        resource={DashboardResources.InventoryItems}
        title="Inventory"
        // ~8 columns plus the expand panel overflow the viewport on smaller
        // screens; without this the page widens and clips the Add filter button.
        sx={scrollableListSx}
        filters={filters}
        // Far too many filters to scan in react-admin's plain Add filter menu.
        actions={<SearchableFilterActions />}
        // Partner-scoped so one partner's saved filters (warehouse, facet
        // values…) aren't reapplied to another partner's inventory.
        storeKey={`${DashboardResources.InventoryItems}.${partnerCode}.listParams`}
        sort={{ field: 'id', order: 'DESC' }}
        perPage={25}
        exporter={false}
        // Without this react-admin renders the datagrid anyway, so a failed
        // query looks like a filter that matched nothing.
        error={<InventoryItemListError />}
      >
        <Datagrid
          bulkActionButtons={false}
          expand={(
            <InventoryItemExpand
              states={states}
              conditions={conditions}
              rejectReasons={rejectReasons}
            />
          )}
          rowClick="expand"
          sx={{
            '& .RaDatagrid-rowCell': { verticalAlign: 'middle' },
            // MUI pads cells 16px each side; across nine columns that is ~144px
            // of pure padding. The nav pane is pinned open (CustomLayout), so
            // the table has the viewport minus the sidebar to fit into — buying
            // the width back here beats dropping a column.
            '& .MuiTableCell-root': { paddingLeft: 1.5, paddingRight: 1.5 },
          }}
        >
          <FunctionField<InventoryItemRecord>
            source="id"
            label="Item"
            render={record => (
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Avatar
                  variant="rounded"
                  src={firstImage(record)}
                  alt={record.catalogItem?.title ?? `Item ${record.id}`}
                  sx={{
                    width: 40,
                    height: 40,
                    bgcolor: 'grey.100',
                    color: 'grey.500',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <CheckroomIcon fontSize="small" />
                </Avatar>
                <Box sx={{ minWidth: 0, maxWidth: 200 }}>
                  {/* Capped and ellipsized rather than wrapped: an unbounded
                      nowrap title sets the column's min-content width, so one
                      long product name widens the whole table. */}
                  <Typography
                    variant="body2"
                    title={record.catalogItem?.title ?? undefined}
                    sx={{
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {record.catalogItem?.title ?? `Item ${record.id}`}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {`#${record.id} · ${record.catalogItem?.sku ?? 'No SKU'}`}
                  </Typography>
                </Box>
              </Stack>
            )}
          />
          <TextField source="catalogItem.brand" label="Brand" sortable={false} emptyText="—" />
          <FunctionField<InventoryItemRecord>
            label="Size / Color"
            sortable={false}
            render={record =>
              [record.catalogItem?.size, record.catalogItem?.display_color ?? record.catalogItem?.color]
                .filter(Boolean)
                .join(' · ') || '—'}
          />
          <FunctionField<InventoryItemRecord>
            label="Condition"
            sortable={false}
            render={record => displayNameFor(conditions, record.condition)}
          />
          <FunctionField<InventoryItemRecord>
            label="State"
            sortable={false}
            render={record => (
              <Chip
                size="small"
                color={stateColor(record.state)}
                label={displayNameFor(states, record.state)}
              />
            )}
          />
          <FunctionField<InventoryItemRecord>
            label="Price"
            sortable={false}
            textAlign="right"
            render={record => (
              <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {formatCents(record.price)}
              </Typography>
            )}
          />
          <TextField source="location.code" label="Location" sortable={false} emptyText="—" />
          <FunctionField<InventoryItemRecord>
            label="Last updated"
            // Shows state_transition_at, and the provider maps this sort field
            // to reware-api's UPDATE_* codes, which order by the same column —
            // so value and ordering agree. Note neither moves for a non-state
            // edit such as a price change; reware-api exposes a true
            // `updated_at` but offers no way to sort by it.
            sortBy="updatedAt"
            render={record => (
              <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                {formatDateTime(record.state_transition_at)}
              </Typography>
            )}
          />
        </Datagrid>
      </List>
    </>
  );
};

export default InventoryItemList;
